import { z } from 'zod'
import { INSIGHT_SELECTION_MAX, insightSpecSchema, insightSummaryOutSchema } from './insights'
import { sectionViewSchema } from './section-views'
import { mediaTypeSchema } from './media'
import { daysAgoSchema, streamSentimentSchema } from './stream'

/**
 * Analytics dashboards, kept by workspace on the server: what was a per-browser store becomes the
 * workspace's, so a colleague, a client and another laptop all open the same dashboards.
 *
 * - One row per dashboard tab, per sub-account (the stream's workspace). The account's own
 *   Analytics page shows its primary sub-account's dashboards — one stream, one set.
 * - Everyone who can view the workspace reads its dashboards; changing them needs edit on it.
 * - `internal` tabs are Clipbook's: never sent to anyone else, and only staff may mark one.
 * - `version` makes a save conditional: a stale one is refused with the current row instead of
 *   silently overwriting a colleague's change.
 * - An edit session's draft stays in the editor's own browser until Publish — only published
 *   layouts live here, so readers never see half an edit.
 */

export const ANALYTICS_CHART_TYPES = [
  'kpi',
  'line',
  'area',
  'bar',
  'donut',
  'table',
  'text',
] as const

export const ANALYTICS_METRICS = [
  'volumeTimeline',
  'sentimentTimeline',
  'topicTrend',
  'topOutlets',
  'topicShare',
  'folderShare',
  'mediaTypeShare',
  'classificationShare',
  'sentimentShare',
  'topStories',
  'echoes',
  'kpiMentions',
  'kpiNetSentiment',
  'kpiNegative',
  'kpiReach',
  /** A Coverage Lab chart: its `spec` is computed over the page's rows. */
  'custom',
  /** A Coverage Lab written briefing, kept as written and rewritten on demand. */
  'summary',
  /** A section the analytics build computed (narratives, whitespace, …): `section` says which
   *  and how it is drawn, and it redraws from the build's latest. */
  'section',
] as const

/** A block's own narrowing on top of the page's filters (Crisis mode's charts are all negative). */
export const chartNarrowSchema = z.object({
  daysAgo: daysAgoSchema.optional(),
  sentiment: streamSentimentSchema.optional(),
  mediaType: mediaTypeSchema.optional(),
})

export const analyticsBlockSchema = z.object({
  id: z.string().min(1).max(100),
  title: z.string().min(1).max(120),
  type: z.enum(ANALYTICS_CHART_TYPES),
  metric: z.enum(ANALYTICS_METRICS),
  narrow: chartNarrowSchema.optional(),
  aiGenerated: z.boolean().optional(),
  /** Share-over-time blocks: counts or percent of each bucket. */
  measure: z.enum(['count', 'percent']).optional(),
  /** Coverage Lab blocks: the chart, described. */
  spec: insightSpecSchema.optional(),
  /** Summary blocks: the briefing as written, what was asked, and when. */
  summary: insightSummaryOutSchema
    .extend({ prompt: z.string().max(1000), writtenAt: z.string() })
    .optional(),
  /** Coverage Lab blocks: the conversation it was made in (`insight_threads`) and the version
   *  saved (0-based), so Refine reopens that conversation there rather than starting over. */
  threadId: z.uuid().optional(),
  threadVersion: z.number().int().min(0).optional(),
  /** Coverage Lab blocks saved from a Library selection: pinned to those articles. */
  hitIds: z.array(z.string().min(1).max(100)).max(INSIGHT_SELECTION_MAX).optional(),
  /** Section blocks: which of the build's sections, and how it is drawn (section-views.ts). */
  section: sectionViewSchema.optional(),
})
export type AnalyticsBlock = z.infer<typeof analyticsBlockSchema>

export const MAX_BLOCKS_PER_ROW = 4

export const layoutRowSchema = z.object({
  id: z.string().min(1).max(100),
  /** Row height in px. */
  height: z.number().int().min(60).max(1200),
  blocks: z.array(analyticsBlockSchema).max(MAX_BLOCKS_PER_ROW),
})
export type LayoutRow = z.infer<typeof layoutRowSchema>

/** A dashboard's whole layout is capped at this much JSON — pinned selections are the bulk. */
export const DASHBOARD_LAYOUT_MAX_BYTES = 1_000_000

export const analyticsLayoutSchema = z
  .object({ rows: z.array(layoutRowSchema).max(40) })
  .refine(
    (layout) => JSON.stringify(layout).length <= DASHBOARD_LAYOUT_MAX_BYTES,
    'This dashboard is too large to save — remove a pinned chart or two.',
  )
export type AnalyticsLayout = z.infer<typeof analyticsLayoutSchema>

export const dashboardSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1).max(60),
  /** Null = the built-in default layout, so improvements to it reach a dashboard nobody edited. */
  layout: analyticsLayoutSchema.nullable(),
  internal: z.boolean(),
  position: z.number().int(),
  version: z.number().int().min(1),
  updatedAt: z.string(),
})
export type Dashboard = z.infer<typeof dashboardSchema>

export const listDashboardsResponseSchema = z.object({
  dashboards: z.array(dashboardSchema),
})
export type ListDashboardsResponse = z.infer<typeof listDashboardsResponseSchema>

export const createDashboardSchema = z.object({
  /** The client may name the id, so a tab keeps its identity from the moment it is made. */
  id: z.uuid().optional(),
  name: z.string().trim().min(1).max(60),
  layout: analyticsLayoutSchema.nullable().default(null),
  internal: z.boolean().default(false),
  position: z.number().int().min(0).max(1000).optional(),
})
export type CreateDashboard = z.input<typeof createDashboardSchema>

export const updateDashboardSchema = z.object({
  /** The version this edit was made against; a newer one on the server refuses it (409). */
  version: z.number().int().min(1),
  name: z.string().trim().min(1).max(60).optional(),
  layout: analyticsLayoutSchema.nullable().optional(),
  internal: z.boolean().optional(),
  position: z.number().int().min(0).max(1000).optional(),
})
export type UpdateDashboard = z.input<typeof updateDashboardSchema>

/** The 409's body: what is on the server now, so the page can show it instead of guessing. */
export const dashboardConflictSchema = z.object({
  error: z.object({ message: z.string(), code: z.literal('conflict') }),
  current: dashboardSchema,
})
export type DashboardConflict = z.infer<typeof dashboardConflictSchema>
